/**
 * AddTaskForm — the input bar at the top of TodayView.
 *
 * Per FWV v8 §30: rendering extracted from TodayView. Pure presentational
 * component; receives initial values + submit handler from the parent.
 *
 * v0.4.0: added recurrence selector (none / daily / weekdays / weekly /
 * biweekly / monthly) so chores like "Feed the dog" can be marked done and
 * automatically re-spawn for the next day.
 */
import { useState } from 'react'
import { Plus, Sparkles } from 'lucide-react'
import type { RecurrenceRule } from '../../db'

export function AddTaskForm({ onSubmit }: {
  onSubmit: (title: string, ep: number, recurrence: RecurrenceRule) => void | Promise<void>
}) {
  const [title, setTitle] = useState('')
  const [ep, setEp] = useState('10')
  const [recurrence, setRecurrence] = useState<RecurrenceRule>('none')

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!title.trim()) return
        onSubmit(title, parseInt(ep, 10) || 10, recurrence)
        setTitle('')
        setRecurrence('none')
      }}
      className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 flex flex-col gap-2"
    >
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What's there to do?"
          className="flex-1 px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-garden-500 min-h-touch"
        />
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 flex items-center gap-1">
            <Sparkles className="w-4 h-4 text-reward-500" />
            <input
              type="number" min="0" value={ep}
              onChange={(e) => setEp(e.target.value)}
              className="w-16 px-2 py-1.5 rounded border border-slate-300"
            />
            EP
          </label>
          <button
            type="submit"
            className="bg-garden-800 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-garden-900 min-h-touch flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <label className="text-slate-600">Repeats:</label>
        <select
          value={recurrence}
          onChange={(e) => setRecurrence(e.target.value as RecurrenceRule)}
          className="px-2 py-1.5 rounded border border-slate-300 bg-white text-sm"
        >
          <option value="none">No</option>
          <option value="daily">Every day</option>
          <option value="weekdays">Every weekday</option>
          <option value="weekly">Every week</option>
          <option value="biweekly">Every 2 weeks</option>
          <option value="monthly">Every month</option>
        </select>
        {recurrence !== 'none' && (
          <span className="text-xs text-slate-500">
            ↻ next occurrence will be created when this one is approved
          </span>
        )}
      </div>
    </form>
  )
}